/**
 * Phase G.5 — Flatten. Stamps a feathered platform into the eroded heightmap
 * under each accepted building footprint, so terrain visually levels out where
 * buildings stand. Returns a HeightmapModule that downstream consumers
 * (terrain renderer, trees, terrain-sampler) bind in place of the raw eroded
 * heightmap.
 */

import { config } from '../config.ts';
import { createStorageTexture2D, createUniformBuffer } from '../gpu/buffers.ts';
import { formats } from '../gpu/formats.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import type { BuildingsModule } from './buildings.ts';
import flattenWgsl from './flatten.wgsl?raw';

export interface FlattenModule extends HeightmapModule {
  /** 1.0 wherever a building platform was stamped (footprint + pad + feather);
   *  0.0 elsewhere. gen/trees.wgsl reads this to skip building lots. */
  readonly occupancy: GPUTexture;
  readonly occupancyView: GPUTextureView;
}

export async function createFlatten(
  device: GPUDevice,
  height: HeightmapModule,
  buildings: BuildingsModule,
): Promise<FlattenModule> {
  const size = config.heightmapSize;

  // Canonical writable copy of the heightmap (storage-bound for in-shader writes,
  // texture-bound for downstream sampling).
  const flattened = createStorageTexture2D(device, [size, size], formats.heightStorage, 'flatten.heightmap');
  // Read-only mirror so the shader can blend against pre-flatten values while writing to `flattened`.
  const snapshot = createStorageTexture2D(device, [size, size], formats.heightStorage, 'flatten.snapshot');
  // Building-footprint occupancy mask. Explicitly zero-filled below, then the
  // pass writes 1.0 inside each platform; trees sample it to skip building lots.
  const occupancy = createStorageTexture2D(device, [size, size], formats.roadStorage, 'flatten.occupancy');

  // Zero-fill source for the occupancy mask. Initializing the texture with an
  // explicit copy (rather than relying on lazy zero-init) is what makes the
  // sparse per-building stamps survive the later sampled read in gen/trees —
  // exactly how `flattened` survives via its copyTextureToTexture below. A
  // freshly created GPUBuffer is zero-initialized by WebGPU.
  const occupancyBytesPerRow = size * 4; // r32float, already a multiple of 256 at size=1024
  const occupancyZero = device.createBuffer({
    label: 'flatten.occupancy.zero',
    size: occupancyBytesPerRow * size,
    usage: GPUBufferUsage.COPY_SRC,
  });

  const copyEnc = device.createCommandEncoder({ label: 'flatten.copy.encoder' });
  copyEnc.copyTextureToTexture(
    { texture: height.texture },
    { texture: flattened },
    { width: size, height: size },
  );
  copyEnc.copyTextureToTexture(
    { texture: height.texture },
    { texture: snapshot },
    { width: size, height: size },
  );
  copyEnc.copyBufferToTexture(
    { buffer: occupancyZero, bytesPerRow: occupancyBytesPerRow, rowsPerImage: size },
    { texture: occupancy },
    { width: size, height: size },
  );
  device.queue.submit([copyEnc.finish()]);
  occupancyZero.destroy();

  // Params: f32 worldSize, f32 worldHeight, u32 heightmapSize, f32 padTexels,
  //         f32 featherTexels, u32 maxInstances, 2× u32 pad → 32 bytes.
  const paramsBuffer = createUniformBuffer(device, 32, 'flatten.params');
  const paramsBytes = new ArrayBuffer(32);
  const paramsU32 = new Uint32Array(paramsBytes);
  const paramsF32 = new Float32Array(paramsBytes);
  paramsF32[0] = config.worldSize;
  paramsF32[1] = config.worldHeight;
  paramsU32[2] = config.heightmapSize >>> 0;
  paramsF32[3] = config.buildingPlatformPadTexels;
  paramsF32[4] = config.buildingPlatformFeatherTexels;
  paramsU32[5] = buildings.maxInstances >>> 0;
  paramsU32[6] = 0;
  paramsU32[7] = 0;
  device.queue.writeBuffer(paramsBuffer, 0, paramsBytes);

  const pipeline = createComputePipeline(device, flattenWgsl, 'main', 'auto', 'flatten');

  const snapshotView = snapshot.createView({ label: 'flatten.snapshot.view' });
  const flattenedView = flattened.createView({ label: 'flatten.heightmap.view' });
  const occupancyView = occupancy.createView({ label: 'flatten.occupancy.view' });

  const bindGroup = device.createBindGroup({
    label: 'flatten.bindGroup',
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: snapshotView },
      { binding: 1, resource: flattenedView },
      { binding: 2, resource: { buffer: buildings.instanceBuffer } },
      { binding: 3, resource: { buffer: buildings.countBuffer } },
      { binding: 4, resource: { buffer: paramsBuffer } },
      { binding: 5, resource: occupancyView },
    ],
  });

  const encoder = device.createCommandEncoder({ label: 'flatten.encoder' });
  const pass = encoder.beginComputePass({ label: 'flatten.pass' });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  // One workgroup column per building, 2×2 tiles per workgroup column → 32×32 texel patch.
  pass.dispatchWorkgroups(buildings.maxInstances, 2, 2);
  pass.end();
  device.queue.submit([encoder.finish()]);

  paramsBuffer.destroy();
  snapshot.destroy();

  return {
    texture: flattened,
    view: flattenedView,
    occupancy,
    occupancyView,
    destroy() {
      flattened.destroy();
      occupancy.destroy();
    },
  };
}
